package org.teddapp.project.ui.finance

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.tedd.db.Finance_transaction
import org.teddapp.project.ui.TeddTokens

/**
 * Toggle global "masquer les soldes" pour la section Finance. Volatil.
 */
val LocalHideBalance = compositionLocalOf { false }

/** Wrapper @Composable de fmtMoney qui consomme automatiquement LocalHideBalance. */
@Composable
internal fun money(n: Long?, currency: String = "XOF", compact: Boolean = false): String =
    fmtMoneyMasked(n, currency, LocalHideBalance.current, compact)

/**
 * Sparkline 14 jours du compte sélectionné : trace le net quotidien.
 */
@Composable
internal fun AccountSparkline(
    accountTxns: List<Finance_transaction>,
    currentAccountId: String?,
    width: Dp = 110.dp,
    height: Dp = 38.dp,
) {
    val days = compute14DayNet(accountTxns, currentAccountId)
    val lastNet = days.lastOrNull()?.second ?: 0L
    val color = if (lastNet >= 0L) TeddTokens.Success else TeddTokens.Danger
    val track = TeddTokens.Line

    Canvas(modifier = Modifier.size(width = width, height = height)) {
        if (days.isEmpty()) return@Canvas
        val w = size.width
        val h = size.height
        val nets = days.map { it.second }
        val minN = nets.min().coerceAtMost(0L).toFloat()
        val maxN = nets.max().coerceAtLeast(0L).toFloat()
        val range = (maxN - minN).coerceAtLeast(1f)
        val xStep = w / (days.size - 1).coerceAtLeast(1).toFloat()
        fun y(v: Long): Float = h - ((v - minN) / range) * h
        val y0 = y(0L)
        drawLine(
            color = track,
            start = Offset(0f, y0),
            end = Offset(w, y0),
            strokeWidth = 0.5.dp.toPx(),
        )
        for (i in 0 until days.size - 1) {
            drawLine(
                color = color,
                start = Offset(i * xStep, y(days[i].second)),
                end = Offset((i + 1) * xStep, y(days[i + 1].second)),
                strokeWidth = 1.6.dp.toPx(),
                cap = StrokeCap.Round,
            )
        }
        val last = Offset((days.size - 1) * xStep, y(days.last().second))
        drawCircle(color = color, radius = 2.dp.toPx(), center = last)
    }
}

private fun compute14DayNet(
    txns: List<Finance_transaction>,
    currentAccountId: String?,
): List<Pair<String, Long>> {
    val today = TODAY_ISO
    val days = (0..13).map { offset -> isoMinusDays(today, 13 - offset) }
    return days.map { iso ->
        val net = txns.asSequence()
            .filter { it.date == iso && it.is_deleted == 0L }
            .sumOf { tx ->
                when {
                    currentAccountId == null -> 0L
                    tx.account_id == currentAccountId -> txSignedAmount(tx)
                    tx.to_account_id == currentAccountId && tx.type == "transfer" -> +tx.amount
                    else -> 0L
                }
            }
        iso to net
    }
}

internal fun isoMinusDays(iso: String, n: Int): String {
    val ref = isoToDayNumber(iso) ?: return iso
    val target = ref - n.toLong()
    return dayNumberToIso(target)
}

internal fun dayNumberToIso(days: Long): String {
    val z = days + 719468L
    val era = (if (z >= 0L) z else z - 146096L) / 146097L
    val doe = z - era * 146097L
    val yoe = (doe - doe / 1460L + doe / 36524L - doe / 146096L) / 365L
    val y = yoe + era * 400L
    val doy = doe - (365L * yoe + yoe / 4L - yoe / 100L)
    val mp = (5L * doy + 2L) / 153L
    val d = doy - (153L * mp + 2L) / 5L + 1L
    val m = if (mp < 10L) mp + 3L else mp - 9L
    val yr = if (m <= 2L) y + 1L else y
    return "${yr.toString().padStart(4, '0')}-" +
        "${m.toString().padStart(2, '0')}-" +
        "${d.toString().padStart(2, '0')}"
}
